import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    BLOOD_GROUPS,
    CITIES,
    COUNTRIES,
    GENDERS,
    ILLNESSES,
    OCCUPATIONS,
} from "@/constants/patient";
import { TID } from "@/constants/testIds";

function Field({ label, required, children, hint }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                {label}
                {required && <span className="text-veda-error"> *</span>}
            </span>
            {children}
            {hint && <span className="text-[10px] text-veda-muted">{hint}</span>}
        </label>
    );
}

const inputCls =
    "border border-veda-border bg-veda-surface px-3 py-2.5 text-sm text-veda-onSurface focus:border-veda-brand focus:outline-none";

export default function PatientOnboarding({ initial, onDone }) {
    const { user } = useAuth();
    const [f, setF] = useState(() => ({
        phone_country: initial?.phone_country || "IN",
        phone: initial?.phone || "",
        dob: initial?.dob || "",
        gender: initial?.gender || "female",
        height_cm: initial?.height_cm || "",
        weight_kg: initial?.weight_kg || "",
        blood_group: initial?.blood_group || "Unknown",
        location: initial?.location || "",
        occupation: initial?.occupation || "Corporate",
        illnesses: initial?.illnesses || [],
    }));
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
    const dial = useMemo(
        () => COUNTRIES.find((c) => c.code === f.phone_country)?.dial || "+",
        [f.phone_country],
    );

    function toggleIllness(name) {
        setF((prev) => {
            const has = prev.illnesses.includes(name);
            if (name === "None of the above") {
                return { ...prev, illnesses: has ? [] : ["None of the above"] };
            }
            const next = has
                ? prev.illnesses.filter((x) => x !== name)
                : [
                      ...prev.illnesses.filter((x) => x !== "None of the above"),
                      name,
                  ];
            return { ...prev, illnesses: next };
        });
    }

    async function submit(e) {
        e.preventDefault();
        // Basic validation
        if (!f.phone.trim()) return toast.error("Phone number is required.");
        if (!f.dob) return toast.error("Date of birth is required.");
        if (!f.height_cm) return toast.error("Height is required.");
        if (!f.weight_kg) return toast.error("Weight is required.");
        if (!f.location) return toast.error("Please select your city.");
        setSaving(true);
        try {
            const payload = {
                ...f,
                height_cm: Number(f.height_cm),
                weight_kg: Number(f.weight_kg),
            };
            const { data } = await api.post("/patient/profile", payload);
            toast.success(
                initial
                    ? "Profile updated."
                    : `Welcome ${user?.name?.split(" ")[0] || ""} — your patient ID is ${data.profile.patient_code}.`,
            );
            onDone?.(data.profile);
        } catch (e2) {
            const detail = e2?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Please check the form and try again.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <form
            onSubmit={submit}
            data-testid={TID.onboardingForm}
            className="mx-auto max-w-3xl"
        >
            <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center bg-veda-brand/10 text-veda-brand">
                    <Sparkles size={16} />
                </span>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                    Patient onboarding
                </p>
            </div>
            <h1 className="font-display mt-2 text-3xl text-veda-onSurface md:text-4xl">
                {initial ? "Update your health profile" : "Tell us about your body."}
            </h1>
            <p className="mt-2 text-sm text-veda-muted">
                This helps every Vaidya, ritual, and recommendation you see
                across Sushen Herbs feel truly personal. Takes about 2 minutes.
            </p>

            {/* Basics */}
            <section className="mt-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    Basics
                </p>
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                    <Field label="Name">
                        <input
                            className={`${inputCls} bg-veda-surface2`}
                            value={user?.name || ""}
                            readOnly
                        />
                    </Field>
                    <Field label="Email">
                        <input
                            className={`${inputCls} bg-veda-surface2`}
                            value={user?.email || ""}
                            readOnly
                        />
                    </Field>
                    <Field label="Phone" required>
                        <div className="flex gap-2">
                            <div className="relative">
                                <select
                                    value={f.phone_country}
                                    onChange={(e) => set("phone_country", e.target.value)}
                                    data-testid={TID.onboardingPhoneCountry}
                                    className={`${inputCls} appearance-none pr-8`}
                                >
                                    {COUNTRIES.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code} {c.dial}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown
                                    size={13}
                                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-veda-muted"
                                />
                            </div>
                            <input
                                type="tel"
                                required
                                placeholder={`${dial} number`}
                                value={f.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                data-testid={TID.onboardingPhone}
                                className={`${inputCls} flex-1`}
                            />
                        </div>
                    </Field>
                    <Field label="Date of birth" required>
                        <input
                            type="date"
                            required
                            value={f.dob}
                            onChange={(e) => set("dob", e.target.value)}
                            data-testid={TID.onboardingDob}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Gender">
                        <div className="flex flex-wrap gap-2">
                            {GENDERS.map((g) => (
                                <button
                                    key={g.key}
                                    type="button"
                                    onClick={() => set("gender", g.key)}
                                    data-testid={TID.onboardingGender(g.key)}
                                    className={`rounded-pill border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                        f.gender === g.key
                                            ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                            : "border-veda-border bg-veda-surface text-veda-onSurface"
                                    }`}
                                >
                                    {g.label}
                                </button>
                            ))}
                        </div>
                    </Field>
                </div>
            </section>

            {/* Body */}
            <section className="mt-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    Body
                </p>
                <div className="mt-3 grid gap-4 md:grid-cols-3">
                    <Field label="Height (cm)" required>
                        <input
                            type="number"
                            min={60}
                            max={230}
                            required
                            value={f.height_cm}
                            onChange={(e) => set("height_cm", e.target.value)}
                            data-testid={TID.onboardingHeight}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Weight (kg)" required>
                        <input
                            type="number"
                            min={20}
                            max={250}
                            required
                            value={f.weight_kg}
                            onChange={(e) => set("weight_kg", e.target.value)}
                            data-testid={TID.onboardingWeight}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Blood group">
                        <select
                            value={f.blood_group}
                            onChange={(e) => set("blood_group", e.target.value)}
                            data-testid={TID.onboardingBloodGroup}
                            className={inputCls}
                        >
                            {BLOOD_GROUPS.map((b) => (
                                <option key={b} value={b}>
                                    {b}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
            </section>

            {/* Life */}
            <section className="mt-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    Life
                </p>
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                    <Field label="City" required>
                        <select
                            value={f.location}
                            onChange={(e) => set("location", e.target.value)}
                            data-testid={TID.onboardingCity}
                            required
                            className={inputCls}
                        >
                            <option value="">Select your city…</option>
                            {CITIES.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Occupation">
                        <select
                            value={f.occupation}
                            onChange={(e) => set("occupation", e.target.value)}
                            data-testid={TID.onboardingOccupation}
                            className={inputCls}
                        >
                            {OCCUPATIONS.map((o) => (
                                <option key={o} value={o}>
                                    {o}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
            </section>

            {/* Illnesses */}
            <section className="mt-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    Health background
                </p>
                <p className="mt-1 text-xs text-veda-muted">
                    Tap all that apply. Your Vaidya sees this — the AI Guide
                    uses it too.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {ILLNESSES.map((name) => {
                        const active = f.illnesses.includes(name);
                        return (
                            <button
                                key={name}
                                type="button"
                                onClick={() => toggleIllness(name)}
                                data-testid={TID.onboardingIllnessChip(
                                    name.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase(),
                                )}
                                className={`inline-flex items-center gap-1 rounded-pill border px-3 py-1.5 text-[11px] font-semibold tracking-[0.02em] ${
                                    active
                                        ? "border-veda-brand bg-veda-brand text-white"
                                        : "border-veda-border bg-veda-surface text-veda-onSurface"
                                }`}
                            >
                                {active && <Check size={11} />}
                                {name}
                            </button>
                        );
                    })}
                </div>
            </section>

            <div className="mt-10 flex flex-wrap items-center justify-end gap-3">
                <button
                    type="submit"
                    disabled={saving}
                    data-testid={TID.onboardingSubmit}
                    className="inline-flex items-center gap-2 bg-veda-brand px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-white disabled:opacity-70"
                >
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    {initial ? "Save changes" : "Create my profile"}
                </button>
            </div>
        </form>
    );
}
